import { APPLICATION } from "constants/app.mjs"
import { useFeatureSwitch } from "modules/featureswitch/FeatureSwitch"
import React, { type ReactNode, useCallback, useEffect, useState } from "react"
import type { MarketDataType, PriceDataType, PriceDataValue } from "data/socket"

type SocketContextType = {
  priceMap: PriceDataType
  ttPriceMap: PriceDataType
  marketDataMap: MarketDataType
}

const SocketContext = React.createContext<SocketContextType>({
  priceMap: {},
  ttPriceMap: {},
  marketDataMap: {},
})

export const useSocket = () => React.useContext(SocketContext)

type RealtimePrice = {
  projectId: string
  priceNow: number
  price24hAgo: number
  change24h: number
  currentATH: number
  currentATL: number
}

const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [priceMap, setPriceMap] = useState<PriceDataType>({})
  const [ttPriceMap, setTTPriceMap] = useState<PriceDataType>({})
  const [marketDataMap, setMarketDataMap] = useState<MarketDataType>({})
  useEffect(() => {
    let socket: SocketIOClient.Socket
    import("data/socket").then((mod) => {
      socket = mod.socket

      if (!socket.connected) {
        socket.connect()
      }

      socket.on("pricedata", (data: PriceDataType) => {
        setPriceMap(data)
      })
      socket.on("marketdata", (data: MarketDataType) => {
        setMarketDataMap((prev) => ({ ...prev, ...data }))
      })
      socket.on("tt-pricedata", (data: Array<RealtimePrice>) => {
        const ttPriceMap: PriceDataType = {}

        data.forEach((item: RealtimePrice) => {
          ttPriceMap[item.projectId] = {
            usd: item.priceNow,
          }
        })

        setTTPriceMap(ttPriceMap)
      })
    })

    return () => {
      if (socket) {
        socket.disconnect()
      }
    }
  }, [])

  return (
    <SocketContext.Provider
      value={{
        priceMap,
        ttPriceMap,
        marketDataMap,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

type ArrayOrString = string | Array<string>

type PricingInfoReturnType<T extends ArrayOrString> = T extends string
  ? PriceDataValue
  : PriceDataType

export const useCurrentPricingInfo = <T extends ArrayOrString>(
  projectId?: T
): PricingInfoReturnType<T> | null => {
  const socketData = useSocket()
  const shouldUseRealtimePricefeed = useFeatureSwitch(
    "SOCKET_USE_REALTIME_PRICEFEED"
  )

  const getPrice = useCallback(
    (id: string): PriceDataValue | null => {
      if (shouldUseRealtimePricefeed && socketData.ttPriceMap[id]) {
        return socketData.ttPriceMap[id]
      }
      if (APPLICATION !== "bloomberg") {
        return socketData.priceMap[id]
      }
      // For example when in bloomberg and ttPriceMap had no hits, we can't fallback to priceMap because that's CoinGecko data
      return null
    },
    [shouldUseRealtimePricefeed, socketData]
  )

  if (!projectId) {
    return null
  }

  const normalizedProjectIds: string[] = Array.isArray(projectId)
    ? projectId
    : [projectId]
  if (!normalizedProjectIds.length) {
    return null
  }

  if (typeof projectId === "string") {
    return getPrice(projectId) as PricingInfoReturnType<T>
  }

  const prices: PriceDataType = {}

  normalizedProjectIds.forEach((pId) => {
    const price = getPrice(pId)
    if (price) {
      prices[pId] = price
    }
  })

  if (!Object.keys(prices).length) {
    return null
  }

  return prices as PricingInfoReturnType<T>
}

export default SocketProvider
